import React, { useMemo, useState } from "react";
import { Pin, Search, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { useContentList, useContentObject } from "../hooks/useContentData";
import { contentFallback } from "../contentDefaults";

const NewsPage = () => {
  const { data: pageHeader } = useContentObject("news.page_header", contentFallback("news.page_header"));
  const { items: newsList } = useContentList("news.list", contentFallback("news.list"));
  const { data: theme } = useContentObject("theme.branding", contentFallback("theme.branding"));
  const newsBg = theme?.bg_news || "https://images.unsplash.com/photo-1773216344148-45afea590cf6?auto=format&fit=crop&w=1920&q=80";
  const bgOpacity = Math.min(100, Math.max(0, Number(theme?.bg_sections_opacity ?? 20))) / 100;
  const [filter, setFilter] = useState("Todas");
  const [query, setQuery] = useState("");

  const categories = useMemo(() => {
    const cats = new Set(newsList.map((n) => n.category).filter(Boolean));
    return ["Todas", ...cats];
  }, [newsList]);

  const filtered = newsList.filter(
    (n) =>
      (filter === "Todas" || n.category === filter) &&
      (!query || n.title.toLowerCase().includes(query.toLowerCase()) || n.excerpt.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <main className="bg-[#0a0a0d] min-h-screen">
      <div className="relative pt-44 pb-16 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${newsBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: bgOpacity,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0d]/60 via-[#0a0a0d]/85 to-[#0a0a0d]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="ornament justify-center inline-flex"><span>{pageHeader?.eyebrow || "Crónicas"}</span></div>
          <h1 className="section-title mt-6 text-4xl md:text-6xl">{pageHeader?.title || "Noticias del Reino"}</h1>
          <p className="mt-5 max-w-2xl mx-auto text-[#b8b3a8]">{pageHeader?.subtitle || ""}</p>
        </div>
      </div>

      <section className="pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a92]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar noticias..."
                className="pl-10 h-11 bg-[#111118] border-[rgba(212,168,67,0.2)] focus:border-[#d4a843] text-[#f0ebe0] font-mono text-sm rounded-sm"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <Filter className="w-4 h-4 text-[#8a8a92] shrink-0" />
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`shrink-0 px-3.5 py-1.5 rounded-sm font-display tracking-[0.18em] uppercase text-[10px] border transition-all ${
                    filter === c
                      ? "bg-[rgba(212,168,67,0.15)] text-[#f5c542] border-[#d4a843]"
                      : "bg-[#14141a] text-[#b8b3a8] border-[rgba(212,168,67,0.15)] hover:text-[#f0ebe0]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.length === 0 && (
              <div className="col-span-full glass rounded-sm p-12 text-center text-[#8a8a92]">
                No se encontraron noticias para esta búsqueda.
              </div>
            )}
            {filtered.map((n) => (
              <article key={n.id} className="group glass rounded-sm overflow-hidden hover:translate-y-[-4px] transition-all">
                <div className="relative h-52 overflow-hidden">
                  <img src={n.image} alt={n.title} className="absolute inset-0 w-full h-full object-cover saturate-50 group-hover:saturate-100 group-hover:scale-110 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111118] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    {n.pinned && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 bg-[#d4a843] text-[#1a1408] font-display tracking-[0.2em] uppercase text-[9px] font-bold rounded-sm">
                        <Pin className="w-3 h-3" /> Pinned
                      </span>
                    )}
                    <span className="px-2.5 py-1 bg-[#0a0a0d]/80 backdrop-blur border border-[rgba(212,168,67,0.4)] text-[#d4a843] font-mono tracking-[0.2em] uppercase text-[9px] rounded-sm">
                      {n.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a843]">{n.date}</span>
                  <h3 className="font-display font-bold text-lg text-[#f0ebe0] mt-2 mb-3 leading-tight group-hover:text-[#f5c542] transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="text-sm text-[#b8b3a8] line-clamp-3">{n.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default NewsPage;
